import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { useFormik } from 'formik';
import { contactSchema } from '../../forms/contactSchema';
import { submitContactForm } from '../../services/contactService';
import FormField from '../../components/common/FormField';
import { showToast } from '../../utils/showToast';
import { getPageContent } from '../../services/pageService';
import Loader from '../../components/common/Loader';
import { usePageMeta } from '../../hooks/usePageMeta';

const ContactPage: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [content, setContent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const contactContent = await getPageContent('contact');
        setContent(contactContent);
      } catch (err) {
        console.error('Error fetching contact content:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchContent();
  }, []);

  const formik = useFormik({
    initialValues: {
      fullName: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    },
    validationSchema: contactSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await submitContactForm(values);
        console.log("Contact form submitted successfully to the backend!");
        setIsSubmitted(true);
        setSubmitting(false);
        resetForm();
      } catch (err: any) {
        let errorMessage = '❌ Failed to send message. Please try again.';
        if (err.message) {
          errorMessage = err.message;
        }
        showToast(errorMessage, 'error');
        console.error("Submission error:", err);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const title = content?.title || 'Contact Us';
  const subtitle = content?.subtitle || 'Reach out to us for any SAP S/4HANA course queries, demo requests or admissions!';

  usePageMeta({
    title,
    description: subtitle,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <Loader />
      </div>
    );
  }
  const phone = content?.phone || '+91 77950 06081';
  const phoneTimings = content?.phoneTimings || 'Mon–Sat: 9:30 AM – 6:30 PM';
  const email = content?.email || 'shreegurucool@gmail.com';
  const emailTimings = content?.emailTimings || '24x7 Email Support';
  const addressTitle = content?.addressTitle || 'ShreeGuruCool teKnologies';
  const addressBody = content?.addressBody || '2nd Floor, Above Nest Bakery, CKB Layout, Marathalli, Bangalore';
  const officeHours = content?.officeHours || [
    'Mon–Fri: 9:30 AM – 6:30 PM',
    'Sat: 9:30 AM – 6:30 PM',
    'Sun: Closed'
  ];
  const mapUrl = content?.mapUrl || 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.3284193438167!2d77.70232817418791!3d12.950824315335247!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1235e3ec686b%3A0xb6fef3cde9aeace1!2sShreeGuruCool%20teKnologies!5e0!3m2!1sen!2sin!4v1751550387459!5m2!1sen!2sin';
  const whatsappNumber = content?.whatsappNumber || '917795006081';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-6">
            <img
              src="/logo.jpeg"
              alt="ShreeGuruCool Logo"
              className="h-16 md:h-20 object-contain drop-shadow-lg rounded"
            />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{title}</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto px-4">
            {subtitle}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Contact Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>
          {isSubmitted ? (
            <div className="text-center py-8" role="status" aria-live="polite">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Message Sent!</h3>
              <p className="text-gray-600">
                Thank you for contacting us. We'll get back to you within 24 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={formik.handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Full Name *"
                  name="fullName"
                  type="text"
                  value={formik.values.fullName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.fullName && formik.errors.fullName ? formik.errors.fullName : ''}
                  placeholder="Your name"
                />
                <FormField
                  label="Email Address *"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && formik.errors.email ? formik.errors.email : ''}
                  placeholder="your@email.com"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.phone && formik.errors.phone ? formik.errors.phone : ''}
                  placeholder="+91 77950 06081"
                />
                <div className="space-y-1">
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700">Subject *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formik.values.subject}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full px-4 py-2 border rounded-lg ${
                      formik.touched.subject && formik.errors.subject ? 'border-red-500' : 'border-gray-300'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="">Select subject</option>
                    <option value="course-inquiry">Course Inquiry</option>
                    <option value="admission">Admission Process</option>
                    <option value="fees">Fees & Payment</option>
                    <option value="placement">Placement Assistance</option>
                    <option value="demo-class">Demo Class</option>
                    <option value="technical-support">Technical Support</option>
                    <option value="other">Other</option>
                  </select>
                  {formik.touched.subject && formik.errors.subject && (
                    <span className="text-xs text-red-600">{formik.errors.subject}</span>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows={5}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    formik.touched.message && formik.errors.message ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Write your message here..."
                ></textarea>
                {formik.touched.message && formik.errors.message && (
                  <span className="text-xs text-red-600">{formik.errors.message}</span>
                )}
              </div>

              <button
                type="submit"
                disabled={formik.isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formik.isSubmitting ? <span>Sending...</span> : <>
                  <Send className="h-4 w-4" /><span>Send Message</span>
                </>}
              </button>
            </form>
          )}
        </div>

        {/* Contact Info, Map, Quick Actions */}
        <div className="space-y-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold mb-6 text-gray-900">Contact Details</h3>
            <div className="space-y-4">
              <div className="flex gap-4">
                <Phone className="text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold">Phone</h4>
                  <p>{phone}</p>
                  <p className="text-sm text-gray-500">{phoneTimings}</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Mail className="text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold">Email</h4>
                  <p>{email}</p>
                  <p className="text-sm text-gray-500">{emailTimings}</p>
                </div>
              </div>
              <div className="flex gap-4">
                <MapPin className="text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold">Address</h4>
                  <p>{addressTitle}</p>
                  <p className="whitespace-pre-line text-gray-600">{addressBody}</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Clock className="text-blue-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold">Office Hours</h4>
                  <div className="space-y-1 text-gray-600">
                    {officeHours.map((hours: string, idx: number) => (
                      <p key={idx}>{hours}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Location</h3>
            <div className="aspect-w-16 aspect-h-9">
              <iframe
                src={mapUrl}
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded-lg"
              ></iframe>
            </div>
          </div>

          {/* Call & WhatsApp */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Need Immediate Help?</h3>
            <div className="space-y-3">
              <button
                onClick={() => window.open(`tel:${phone.replace(/\s+/g, '')}`)}
                className="w-full bg-white/20 hover:bg-white/30 py-2 px-4 rounded-lg flex items-center justify-center space-x-2"
              >
                <Phone className="h-4 w-4" />
                <span>Call Now</span>
              </button>
              <button
                onClick={() =>
                  window.open(
                    `https://wa.me/${whatsappNumber}?text=Hi, I need help with SAP S/4HANA courses`
                  )
                }
                className="w-full bg-green-600 hover:bg-green-700 py-2 px-4 rounded-lg flex items-center justify-center space-x-2"
              >
                <span>WhatsApp</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;